import React from "react";
import jsonData from "./constants/teststedy.json";
import { TemplateWrapped } from "../Layout";
import "./index.css";
import { v4 as uuid } from "uuid";

const sectionColors = {
  meta: "blue",
  provider: "green",
  subscriber: "purple",
  payer: "orange",
  planInformation: "indigo",
  planDateInformation: "teal",
  planStatus: "cyan",
  benefitsInformation: "red",
};

const SectionBox = ({ title, children }) => (
  <div
    className="bg-white mb-2 border border-slate-200 border-card"
    style={{ marginTop: "0.875rem" }}
  >
    <div className="card-header-real-time">
      <h4
        className="font-semibold leading-none tracking-tight flex items-center gap-2"
        style={{
          marginBottom: "0 !important",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          color: "#0f172a",
          fontSize: "1.150rem",
          margin: 0,
        }}
      >
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            className="lucide lucide-circle-check h-4 w-4 text-slate-600"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
        </div>
        {title}
      </h4>
    </div>
    <div className="grid-container gap-4">{children}</div>
  </div>
);

function renderObject(value, label) {
  const entries = Object.entries(value);
  return (
    <SectionBox key={uuid()} title={formatKey(label)}>
      <React.Fragment>
        {entries.map(([key, value], index) => (
          <InfoItem
            key={key}
            label={formatKey(key)}
            value={value}
            isFirstItem={index === 0}
            isLastItem={index === entries.length - 1}
          />
        ))}
      </React.Fragment>
    </SectionBox>
  );
}

function renderSimpleArray(arr, label) {
  return (
    <InfoItem
      label={formatKey(label)}
      value={arr.join(", ")}
      isFirstItem={true}
      isLastItem={false}
    />
  );
}

function renderArrayOfObjects(arr, label) {
  return arr.map((obj, idx) => {
    const entries = Object.entries(obj);
    return (
      <SectionBox key={idx} title={`${label} ${idx + 1}`}>
        {entries.map(([key, val], index) => (
          <div key={key} style={{ marginTop: index === 0 ? "0.875rem" : 0 }}>
            <InfoItem
              label={formatKey(key)}
              value={val}
              isFirstItem={index === 0}
              isLastItem={index === entries.length - 1}
            />
          </div>
        ))}
      </SectionBox>
    );
  });
}

function renderPrimitive(label, value, isFirstItem, isLastItem) {
  return (
    <div
      className="grid-container-values"
      style={{
        alignItems: "center",
        borderBottom: isLastItem ? "" : "1px solid #e5e7eb",
        marginTop: isFirstItem ? "0.875rem" : 0,
      }}
    >
      <span className="text-label">{label}</span>
      <div className="text-value">{String(value)}</div>
    </div>
  );
}

const InfoItem = ({ label, value, isFirstItem, isLastItem }) => (
  <React.Fragment>
    {typeof value === "object" && !Array.isArray(value)
      ? renderObject(value, label)
      : Array.isArray(value) && isSimpleArray(value)
        ? renderSimpleArray(value, label)
        : Array.isArray(value) && isArrayOfObjects(value)
          ? renderArrayOfObjects(value, label)
          : renderPrimitive(label, value, isFirstItem, isLastItem)}
  </React.Fragment>
);

const renderSection = (key, value) => {
  const color = sectionColors[key] || "gray";

  if (Array.isArray(value)) {
    return (
      <SectionBox key={key} title={formatKey(key)} color={color}>
        {value.map((item, index) => (
          <div key={index} className=" bg-gray-50">
            {Object.entries(item).map(([k, v], index2) => (
              <div className="mt-1">
                <InfoItem
                  key={k}
                  label={formatKey(k)}
                  value={v}
                  isFirstItem={index2 === 0}
                  isLastItem={index2 === Object.keys(item).length - 1}
                />
              </div>
            ))}
          </div>
        ))}
      </SectionBox>
    );
  }

  if (typeof value === "object") {
    return (
      <SectionBox key={key} title={formatKey(key)} color={color}>
        {Object.entries(value).map(([k, v], index2) => (
          <InfoItem
            key={k}
            label={formatKey(k)}
            value={v}
            isFirstItem={index2 === 0}
            isLastItem={index2 === Object.keys(value).length - 1}
          />
        ))}
      </SectionBox>
    );
  }

  return (
    <SectionBox key={key} title={formatKey(key)} color={color}>
      <InfoItem
        label={formatKey(key)}
        value={value}
        isFirstItem={true}
        isLastItem={true}
      />
    </SectionBox>
  );
};

const formatKey = (key) =>
  key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/_/g, " ");

function isSimpleArray(arr) {
  if (!Array.isArray(arr)) return false;

  return arr.every(
    (item) =>
      typeof item === "string" ||
      typeof item === "number" ||
      typeof item === "boolean" ||
      item === null,
  );
}

function isArrayOfObjects(arr) {
  if (!Array.isArray(arr)) return false;

  return arr.every(
    (item) => typeof item === "object" && item !== null && !Array.isArray(item),
  );
}

const JsonViewerContainer = () => {
  return (
    <TemplateWrapped
      title="Insurance Verification | Real Time"
      headerButtons={<React.Fragment></React.Fragment>}
    >
      <div
        className=" mx-auto text-gray-800 font-sans mb-2"
        style={{ maxWidth: "800px" }}
      >
        {Object.entries(jsonData).map(([key, value]) =>
          renderSection(key, value),
        )}
      </div>
    </TemplateWrapped>
  );
};
export default JsonViewerContainer;
